import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Radio, ShieldOff, Unlink } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton, SkeletonRows } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/states/ErrorState';
import { EmptyState } from '@/components/states/EmptyState';
import { OnlineDot } from '@/components/domain/OnlineDot';
import { RelativeTime } from '@/components/domain/RelativeTime';
import { BaseMap } from '@/components/map/BaseMap';
import { getDevice } from '@/api/endpoints/devices';
import { listTelemetry } from '@/api/endpoints/telemetry';
import { queryKeys } from '@/api/queryKeys';
import { useLiveTelemetry } from '@/api/ws';
import { useDeviceStatus } from '@/features/shared/queries';
import { absoluteTime, coordinates, resultantG, speedLabel, titleCase } from '@/lib/format';
import { TelemetryChart } from './TelemetryChart';
import { ExportCsvDialog } from './ExportCsvDialog';
import { UnclaimDialog } from './UnclaimDialog';
import { RevokeDialog } from './RevokeDialog';
import { cn } from '@/lib/cn';

const LIVE_STATUS_COPY = {
  connecting: { text: 'Connecting to live feed…', className: 'text-ink-muted' },
  live: { text: 'Live', className: 'text-ok' },
  degraded: { text: 'Live feed unavailable — showing last reported position', className: 'text-warn' },
  unauthorized: { text: 'You do not have live access to this device', className: 'text-bad' },
  offline: { text: 'Live feed off', className: 'text-ink-muted' },
} as const;

export function DeviceDetailPage() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const numericId = Number(deviceId);
  const valid = Number.isInteger(numericId) && numericId > 0;

  const [exporting, setExporting] = useState(false);
  const [unclaiming, setUnclaiming] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const device = useQuery({
    queryKey: queryKeys.device(numericId),
    queryFn: () => getDevice(numericId),
    enabled: valid,
  });

  const history = useQuery({
    queryKey: queryKeys.telemetry(numericId, 200),
    queryFn: () => listTelemetry(numericId, 200),
    enabled: valid,
  });

  const status = useDeviceStatus();
  const live = useLiveTelemetry({ deviceId: valid ? numericId : null });

  if (!valid) {
    return <ErrorState error={new Error('That device address is not valid.')} title="Device not found" />;
  }

  if (device.isPending) {
    return (
      <>
        <PageHeader title="Device" />
        <Card>
          <SkeletonRows rows={4} />
        </Card>
      </>
    );
  }

  if (device.isError) {
    return (
      <>
        <Link to="/vehicles" className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-accent">
          <ArrowLeft className="size-4" aria-hidden />
          Back to vehicles
        </Link>
        <ErrorState
          error={device.error}
          onRetry={device.refetch}
          title="This device is not available"
        />
      </>
    );
  }

  const record = device.data;
  const liveStatus = LIVE_STATUS_COPY[live.status];
  const deviceStatus = (status.data ?? []).find((entry) => entry.id === numericId);

  // Prefer the streamed position; fall back to the polled one when the socket is down.
  const livePoint =
    live.latest?.gps_lat != null && live.latest.gps_lon != null
      ? ([live.latest.gps_lat, live.latest.gps_lon] as [number, number])
      : deviceStatus?.gps_lat != null && deviceStatus.gps_lon != null
        ? ([deviceStatus.gps_lat, deviceStatus.gps_lon] as [number, number])
        : null;

  const canManage = record.state === 'claimed';

  return (
    <>
      <Link to="/vehicles" className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-accent hover:text-accent-hover">
        <ArrowLeft className="size-4" aria-hidden />
        Back to vehicles
      </Link>

      <PageHeader
        title={record.serial_number}
        description={`${titleCase(record.state)}${record.firmware_version ? ` · firmware ${record.firmware_version}` : ''}`}
        action={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setExporting(true)}>
              <Download className="size-4" aria-hidden />
              Export data
            </Button>
            {canManage && record.device_uid && (
              <>
                <Button size="sm" onClick={() => setUnclaiming(true)}>
                  <Unlink className="size-4" aria-hidden />
                  Unlink
                </Button>
                <Button variant="danger" size="sm" onClick={() => setRevoking(true)}>
                  <ShieldOff className="size-4" aria-hidden />
                  Report lost
                </Button>
              </>
            )}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader
            title="Live position"
            action={
              <span className={cn('inline-flex items-center gap-1.5 text-[13px]', liveStatus.className)}>
                <Radio className="size-3.5" aria-hidden />
                {liveStatus.text}
              </span>
            }
          />
          {livePoint === null ? (
            <EmptyState
              icon={Radio}
              title="No position yet"
              description="The device reports its location once it has power and a GPS fix."
            />
          ) : (
            <div className="p-4">
              <BaseMap
                markers={[
                  {
                    id: record.id,
                    position: livePoint,
                    title: record.serial_number,
                    subtitle: coordinates(livePoint[0], livePoint[1]),
                  },
                ]}
                followPoint={livePoint}
                height="h-96"
              />
            </div>
          )}
        </Card>

        <Card>
          <CardHeader title="Status" />
          <CardBody className="space-y-3 text-[13px]">
            <div>
              <p className="text-ink-muted">Reporting</p>
              <div className="mt-0.5">
                <OnlineDot online={deviceStatus?.is_online ?? false} />
              </div>
            </div>
            <div>
              <p className="text-ink-muted">Last seen</p>
              <p className="mt-0.5">
                <RelativeTime value={deviceStatus?.last_seen ?? record.last_seen} />
              </p>
            </div>
            <div>
              <p className="text-ink-muted">Position</p>
              <p className="mt-0.5 text-ink" data-numeric>
                {coordinates(livePoint?.[0] ?? null, livePoint?.[1] ?? null)}
              </p>
            </div>
            <div>
              <p className="text-ink-muted">Speed</p>
              <p className="mt-0.5 text-ink" data-numeric>
                {speedLabel(live.latest?.speed ?? null)}
              </p>
            </div>
            <div>
              <p className="text-ink-muted">Linked since</p>
              <p className="mt-0.5 text-ink">{absoluteTime(record.created_at)}</p>
            </div>
          </CardBody>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader
          title="Recent motion"
          description="Impact force and speed. The crash threshold is 3.5 g."
        />
        {history.isPending ? (
          <div className="p-4">
            <Skeleton className="h-56 w-full" />
          </div>
        ) : history.isError ? (
          <ErrorState error={history.error} onRetry={history.refetch} />
        ) : (history.data ?? []).length === 0 ? (
          <EmptyState
            icon={Radio}
            title="No telemetry recorded"
            description="Readings appear here once the device starts reporting."
          />
        ) : (
          <CardBody>
            <TelemetryChart rows={history.data ?? []} />
          </CardBody>
        )}
      </Card>

      {live.rows.length > 0 && (
        <Card className="mt-4">
          <CardHeader title="Live readings" description="Streaming directly from the device." />
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="border-b border-line text-left text-ink-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Time</th>
                  <th className="px-4 py-2 font-medium">Impact (g)</th>
                  <th className="px-4 py-2 font-medium">Speed</th>
                  <th className="px-4 py-2 font-medium">Position</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {live.rows.map((row) => {
                  const g = resultantG(row.accel_x, row.accel_y, row.accel_z);
                  return (
                    <tr key={`${row.id}-${row.timestamp}`}>
                      <td className="whitespace-nowrap px-4 py-2 text-ink-secondary">
                        {absoluteTime(row.timestamp)}
                      </td>
                      <td className={cn('px-4 py-2', g >= 3.5 ? 'font-semibold text-bad' : 'text-ink')}>
                        {g.toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-ink">{speedLabel(row.speed)}</td>
                      <td className="px-4 py-2 text-ink-secondary">
                        {coordinates(row.gps_lat, row.gps_lon)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ExportCsvDialog
        open={exporting}
        onOpenChange={setExporting}
        deviceId={numericId}
        serialNumber={record.serial_number}
      />
      {record.device_uid && (
        <>
          <UnclaimDialog
            open={unclaiming}
            onOpenChange={setUnclaiming}
            deviceUid={record.device_uid}
            serialNumber={record.serial_number}
          />
          <RevokeDialog
            open={revoking}
            onOpenChange={setRevoking}
            deviceUid={record.device_uid}
            serialNumber={record.serial_number}
          />
        </>
      )}
    </>
  );
}
